import { useEffect, useState } from "react";
import { Truck, Loader2, Plus, Trash2, Lock, CalendarDays, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const newStop = () => ({ _k: Math.random().toString(36).slice(2), place: "", date: "", time_from: "", time_to: "", note: "", menu_id: "" });
const fixYear = (d) => { const m = /^(\d{1,4})-(\d{2}-\d{2})$/.exec(d || ""); return m && +m[1] < 100 ? `${(+m[1] + 2000).toString().padStart(4, "0")}-${m[2]}` : (d || ""); };
const withKeys = (ft) => ({ enabled: false, place: "", date: "", time_from: "", time_to: "", note: "", ...(ft || {}), stops: (ft?.stops || []).map((s) => ({ ...s, _k: s._k || Math.random().toString(36).slice(2) })) });

export const FoodTruckDialog = ({ restaurant, onSaved }) => {
  const { user } = useAuth();
  const canSchedule = !!user?.subscription?.features?.foodtruck_schedule;
  const canMenu = !!user?.subscription?.features?.foodtruck_menu;
  const [menus, setMenus] = useState([]);
  const [newMenuFor, setNewMenuFor] = useState(null);
  const [newMenuName, setNewMenuName] = useState("");
  useEffect(() => { if (canMenu) api.get("/menus").then((r) => setMenus(r.data.filter((m) => m.is_foodtruck))).catch(() => {}); }, [canMenu]);
  const createMenu = async (stopKey) => {
    try {
      const { data } = await api.post("/menus", { name: newMenuName.trim() || "Menù FoodTruck", is_foodtruck: true });
      setMenus((ms) => [...ms, data]);
      setFt((f) => (stopKey === "single" ? { ...f, menu_id: data.id } : { ...f, stops: f.stops.map((s) => (s._k === stopKey ? { ...s, menu_id: data.id } : s)) }));
      setNewMenuFor(null); setNewMenuName("");
      toast.success(`Menù "${data.name}" creato: aggiungi i piatti dal Costruttore menù.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const duplicateMenu = async (stopKey, srcId) => {
    const src = menus.find((m) => m.id === srcId);
    const name = window.prompt("Nome del nuovo menù serata:", `${src?.name || "Menù"} (copia)`);
    if (name === null) return;
    try {
      const { data } = await api.post(`/menus/${srcId}/duplicate`, { name });
      setMenus((ms) => [...ms, data]);
      setFt((f) => (stopKey === "single" ? { ...f, menu_id: data.id } : { ...f, stops: f.stops.map((s) => (s._k === stopKey ? { ...s, menu_id: data.id } : s)) }));
      toast.success(`Menù "${data.name}" duplicato con tutti i piatti.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const renderMenuPicker = (value, onChange, testid, stopKey) => canMenu ? (
    <div className="space-y-2 rounded-lg bg-[#5aa469]/8 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <UtensilsCrossed className="h-4 w-4 text-[#5aa469] shrink-0" />
        <select value={value || ""} onChange={onChange} className="h-9 flex-1 min-w-0 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid={testid}>
          <option value="">Menù FoodTruck della serata: nessuno</option>
          {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <button type="button" onClick={() => { setNewMenuFor(stopKey); setNewMenuName(""); }} className="text-xs text-[#4a8a56] underline whitespace-nowrap" data-testid={`${testid}-create`}>+ Crea menù serata</button>
      </div>
      {newMenuFor === stopKey && (
        <div className="flex gap-2">
          <Input value={newMenuName} onChange={(e) => setNewMenuName(e.target.value)} placeholder="Nome, es. Sagra Sant'Angelo 11/09" className="h-9" data-testid={`${testid}-new-name`} />
          <Button type="button" size="sm" onClick={() => createMenu(stopKey)} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white" data-testid={`${testid}-new-save`}>Crea</Button>
        </div>
      )}
      {value && (
        <div className="flex flex-wrap gap-3 text-xs">
          <Link to={`/dashboard/menu?menu=${value}`} className="text-gray-500 underline" data-testid={`${testid}-edit`}>Modifica piatti di questo menù →</Link>
          <button type="button" onClick={() => duplicateMenu(stopKey, value)} className="text-[#4a8a56] underline" data-testid={`${testid}-duplicate`}>Duplica per questa serata</button>
        </div>
      )}
    </div>
  ) : null;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ft, setFt] = useState(() => withKeys(restaurant.foodtruck));
  const set = (k) => (e) => setFt((f) => ({ ...f, [k]: e.target.value }));
  const setStop = (k, field) => (e) => setFt((f) => ({ ...f, stops: f.stops.map((s) => (s._k === k ? { ...s, [field]: e.target.value } : s)) }));
  const removeStop = (k) => setFt((f) => ({ ...f, stops: f.stops.filter((s) => s._k !== k) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const stops = canSchedule ? ft.stops.map(({ _k, ...s }) => ({ ...s, date: fixYear(s.date) })) : [];
      const bad = stops.find((s) => !s.place.trim() || !s.date);
      if (bad) { toast.error(!bad.place.trim() ? "Inserisci il luogo di ogni tappa." : `Inserisci la data della tappa «${bad.place}».`); setBusy(false); return; }
      const payload = { ...ft, date: fixYear(ft.date), stops };
      const { data } = await api.put("/restaurant", { foodtruck: payload });
      onSaved(data);
      toast.success(ft.enabled ? "FoodTruck pubblicato sul menù" : "FoodTruck salvato (non visibile ai clienti)");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const active = restaurant.foodtruck?.enabled;
  const sortedStops = [...ft.stops].sort((a, b) => (a.date + a.time_from).localeCompare(b.date + b.time_from));

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setFt(withKeys(restaurant.foodtruck)); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className={`rounded-full border-gray-300 bg-white hover:bg-gray-50 font-semibold ${active ? "border-[#5aa469] text-[#4a8a56]" : ""}`} data-testid="foodtruck-btn">
          <Truck className="h-4 w-4 mr-2" /> FoodTruck {active && <span className="ml-2 h-2 w-2 rounded-full bg-[#5aa469]" />}
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="foodtruck-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Truck className="h-5 w-5 text-[#5aa469]" /> FoodTruck</DialogTitle>
          <DialogDescription>Indica dove e quando lavorerai: i clienti lo vedranno in cima al tuo menù.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-gray-50 p-3">
            <Label htmlFor="ft-enabled" className="font-medium">Mostra nel menù pubblico</Label>
            <Switch id="ft-enabled" checked={ft.enabled} onCheckedChange={(v) => setFt((f) => ({ ...f, enabled: v }))} data-testid="foodtruck-enabled" />
          </div>

          {canSchedule ? (
            <div className="space-y-3" data-testid="foodtruck-schedule">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#5aa469]" /> Programma della settimana</p>
                <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => setFt((f) => ({ ...f, stops: [...f.stops, newStop()] }))} data-testid="foodtruck-add-stop">
                  <Plus className="h-4 w-4 mr-1" /> Aggiungi tappa
                </Button>
              </div>
              <p className="text-xs text-gray-500">Nel menù viene mostrata automaticamente la prossima tappa in programma (ora italiana). Luogo e data sono obbligatori per ogni tappa.{canMenu && " Per ogni serata puoi creare un menù FoodTruck dedicato (separato dal menù del locale, solo consultabile): i clienti lo aprono dal pulsante sopra il box della tappa."}</p>
              {sortedStops.length === 0 && <p className="text-sm text-gray-400 rounded-xl border border-dashed p-4 text-center">Nessuna tappa. Aggiungi la prima!</p>}
              {sortedStops.map((s, i) => (
                <div key={s._k} className="rounded-xl border border-gray-200 p-3 space-y-2" data-testid={`foodtruck-stop-${i}`}>
                  <div className="flex gap-2">
                    <Input value={s.place} onChange={setStop(s._k, "place")} placeholder="Luogo *" className="flex-1" data-testid={`stop-place-${i}`} />
                    <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:bg-red-50 shrink-0" onClick={() => removeStop(s._k)} data-testid={`stop-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input type="date" value={s.date} min="2020-01-01" max="2100-12-31" onChange={setStop(s._k, "date")} data-testid={`stop-date-${i}`} />
                    <Input type="time" value={s.time_from} onChange={setStop(s._k, "time_from")} data-testid={`stop-from-${i}`} />
                    <Input type="time" value={s.time_to} onChange={setStop(s._k, "time_to")} data-testid={`stop-to-${i}`} />
                  </div>
                  <Input value={s.note} onChange={setStop(s._k, "note")} placeholder="Nota (opzionale)" data-testid={`stop-note-${i}`} />
                  {renderMenuPicker(s.menu_id, setStop(s._k, "menu_id"), `stop-menu-${i}`, s._k)}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>Luogo *</Label>
                <Input value={ft.place} onChange={set("place")} placeholder="Es. Piazza Duomo, Milano" data-testid="foodtruck-place" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Data</Label><Input type="date" value={ft.date} min="2020-01-01" max="2100-12-31" onChange={set("date")} data-testid="foodtruck-date" /></div>
                <div className="space-y-1.5"><Label>Dalle</Label><Input type="time" value={ft.time_from} onChange={set("time_from")} data-testid="foodtruck-from" /></div>
                <div className="space-y-1.5"><Label>Alle</Label><Input type="time" value={ft.time_to} onChange={set("time_to")} data-testid="foodtruck-to" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Nota (opzionale)</Label>
                <Input value={ft.note} onChange={set("note")} placeholder="Es. Solo contanti, ci trovate accanto alla fontana" data-testid="foodtruck-note" />
              </div>
              <Link to="/dashboard/billing" className="flex items-center gap-2 text-xs text-gray-500 rounded-xl bg-gray-50 p-3 hover:bg-gray-100" data-testid="foodtruck-upsell">
                <Lock className="h-3.5 w-3.5" /> Con i piani Avanzato, Pro e Business puoi programmare più tappe per la settimana: il menù mostra da solo la prossima.
              </Link>
            </>
          )}

          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-[#5aa469] hover:bg-[#4a8a56] text-white font-semibold" data-testid="foodtruck-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
